/**
 * ステージエディタの中身（§6 デバッグツールは最優先で作る）。
 *
 * ここは sim 側なので DOM も Canvas も知らない（§4.4）。
 * UI は debug/ が持ち、この層は「盤面を書き換える」「盤面を検算する」
 * 「floors.json を吐く」の3つだけを担当する。
 *
 * 検算がこのファイルの主役。汎用エンジンのタイルマップエディタを持ってきても、
 * 「穴の真上が空いているか」「老猫がその階で分断されていないか」
 * 「この階から上へ道を通せるか」はどこへ行っても自作になる（§4.6）。
 * だったら、盤面を触ったその場で走るようにしておくのが一番安い。
 */
import { bfs } from './graph';
import { BUILT, EMPTY, STRUCTURE, isNode, keyOf, tileAt } from './grid';
import { itemCells } from './items';
import { MATERIALS, MATERIAL_IDS, MaterialBundle } from './materials';
import {
  BUILDING,
  FIXTURE_KINDS,
  SALVAGE_KINDS,
  fixtureKindOf,
  salvageKindOf,
  slabGapsAt,
} from './state';
import { World, canPlaceItem, graphFor, rebuildGraphs } from './world';
import MEMORY_DATA from '../data/memories.json';

/** 編集で置ける物 */
export type Brush =
  | { kind: 'erase' }
  | { kind: 'solid' }
  | { kind: 'salvage'; id: string }
  | { kind: 'fixture'; id: string }
  | { kind: 'bowl' }
  | { kind: 'water' }
  | { kind: 'entrance' }
  /** 居着いて出る思い出を、掴んで動かす */
  | { kind: 'memory' };

export const SALVAGE_IDS = SALVAGE_KINDS.map((k) => k.id);
export const FIXTURE_IDS = FIXTURE_KINDS.map((k) => k.id);

/** 一筆ぶんの編集。地形が変わるものは必ずグラフを組み直す（§5.1） */
export function paint(world: World, c: number, r: number, brush: Brush): void {
  const grid = world.state.grid;
  if (c <= 0 || r <= 0 || c >= grid.cols - 1 || r >= grid.rows - 1) {
    // 外周は建物の外側。触らせない（触ると猫が落ちる先が無くなる）
    if (brush.kind !== 'entrance') return;
  }
  const k = keyOf(grid, c, r);

  switch (brush.kind) {
    case 'erase':
      grid.tiles[k] = EMPTY;
      grid.salvage[k] = 0;
      grid.wear[k] = 0;
      world.state.fixtures = world.state.fixtures.filter((f) => !(f.c === c && f.r === r));
      break;
    case 'solid':
      world.state.fixtures = world.state.fixtures.filter((f) => !(f.c === c && f.r === r));
      grid.salvage[k] = 0;
      grid.tiles[k] = STRUCTURE;
      break;
    case 'salvage': {
      const index = SALVAGE_IDS.indexOf(brush.id);
      if (index < 0) return;
      world.state.fixtures = world.state.fixtures.filter((f) => !(f.c === c && f.r === r));
      grid.tiles[k] = 4; // SALVAGE
      grid.salvage[k] = index + 1;
      grid.wear[k] = 0;
      break;
    }
    case 'fixture': {
      if (!fixtureKindOf(brush.id)) return;
      grid.tiles[k] = EMPTY;
      grid.salvage[k] = 0;
      world.state.fixtures = world.state.fixtures.filter((f) => !(f.c === c && f.r === r));
      world.state.fixtures.push({ id: world.state.nextFixtureId++, c, r, kind: brush.id, wear: 0 });
      break;
    }
    case 'bowl':
      world.state.bowl = { c, r };
      break;
    case 'water':
      world.state.water = { c, r };
      break;
    case 'entrance':
      world.state.entrance = { c, r };
      break;
    case 'memory':
      // 掴んでいる物があれば置く。無ければ、その辺にある物を掴む
      if (world.heldMemory) {
        const m = world.state.memories.find((x) => x.id === world.heldMemory);
        if (m) {
          m.c = c;
          m.r = r;
        }
        world.heldMemory = null;
      } else {
        const near = world.state.memories
          .filter((m) => m.trigger.on === 'dwell')
          .find((m) => Math.abs(m.c - c) <= 1 && Math.abs(m.r - r) <= 1);
        world.heldMemory = near?.id ?? null;
      }
      break;
  }
  rebuildGraphs(world);
  world.dirty = true;
}

/**
 * 範囲を一気に塗る。
 * スマホで壁を1マスずつ叩くのは無理があるので、角を2回押したら埋まる形にした。
 */
export function paintRect(
  world: World,
  a: { c: number; r: number },
  b: { c: number; r: number },
  brush: Brush,
): void {
  const c0 = Math.min(a.c, b.c);
  const c1 = Math.max(a.c, b.c);
  const r0 = Math.min(a.r, b.r);
  const r1 = Math.max(a.r, b.r);
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) paint(world, c, r, brush);
  }
}

// ---- 検算 ---------------------------------------------------------------

export interface Issue {
  level: 'error' | 'warn';
  text: string;
  at: { c: number; r: number } | null;
}

/** その階の airspace（立ち面から天井の1つ下まで） */
function floorRows(index: number): { standRow: number; topRow: number } {
  const floor = BUILDING.floors[index];
  const above = BUILDING.floors[index + 1];
  const topRow = above ? above.standRow + 2 : 4;
  return { standRow: floor.standRow, topRow };
}

/**
 * その階の立ち面が、老猫の足でつながっているか。
 *
 * 床の穴で切れるのは当たり前（穴の上のマスには立てない）なので、
 * **穴で区切った区間ごと**に見る。区間の中で行き来できないなら、
 * それは2段の瓦礫のような「老猫が越えられない障害物」が入っている。
 */
function floorIsSplit(world: World, standRow: number): { c: number; r: number } | null {
  const grid = world.state.grid;
  const graph = graphFor(world, 'elder');
  const holes = new Set(slabGapsAt(standRow + 1));

  let segment: number[] = [];
  const check = (): { c: number; r: number } | null => {
    if (segment.length < 2) return null;
    const seen = bfs(graph, [segment[0]]);
    const lost = segment.find((k) => !seen.has(k));
    return lost === undefined ? null : { c: lost % grid.cols, r: standRow };
  };

  for (let c = 1; c < grid.cols - 1; c++) {
    if (holes.has(c)) {
      const found = check();
      if (found) return found;
      segment = [];
      continue;
    }
    const k = keyOf(grid, c, standRow);
    if (graph.nodes.has(k)) segment.push(k);
  }
  return check();
}

/** 軽い検算。編集のたびに走らせても平気なものだけ */
export function validate(world: World): Issue[] {
  const state = world.state;
  const grid = state.grid;
  const out: Issue[] = [];

  const standable = (c: number, r: number): boolean => isNode(grid, c, r);

  // 餌場・水場・入口は猫が立てるマスに無いと意味がない
  for (const [name, spot] of [
    ['餌場', state.bowl],
    ['水場', state.water],
    ['入口', state.entrance],
  ] as const) {
    if (!standable(spot.c, spot.r)) {
      out.push({ level: 'error', text: `${name}が猫の立てないマスにある`, at: spot });
    }
  }
  if (state.bowl.r !== BUILDING.floors[0].standRow || state.water.r !== BUILDING.floors[0].standRow) {
    out.push({ level: 'error', text: '餌場と水場は1階の立ち面に置くこと（§2.7）', at: null });
  }

  // 天井から下げる飾りは、真上が固くないと浮いて見える
  for (const f of state.fixtures) {
    if (tileAt(grid, f.c, f.r) !== EMPTY) {
      out.push({ level: 'error', text: `飾りが固いマスに重なっている（${f.kind}）`, at: f });
    }
    const kind = fixtureKindOf(f.kind);
    if (kind?.mount === 'ceiling' && tileAt(grid, f.c, f.r - 1) === EMPTY) {
      out.push({ level: 'warn', text: `天井から下げる飾りの真上が空いている（${f.kind}）`, at: f });
    }
  }

  // 場所の決まっている思い出は、猫が居着ける場所にないと永遠に出ない。
  // 壊して出る物は場所を持たない（壊れた所に出る）ので、ここでは見ない
  for (const m of state.memories) {
    if (m.trigger.on !== 'dwell') continue;
    if (!standable(m.c, m.r)) {
      out.push({ level: 'warn', text: `思い出が猫の立てないマスにある：${m.line}`, at: m });
    }
  }

  for (let i = 0; i < BUILDING.floors.length; i++) {
    const floor = BUILDING.floors[i];
    const { standRow } = floorRows(i);

    // 立ち面が老猫の足で分断されていないか
    const split = floorIsSplit(world, standRow);
    if (split) {
      out.push({
        level: 'warn',
        text: `${floor.name}の立ち面が分断されている（老猫の足で行き来できない）`,
        at: split,
      });
    }

    // 天井の穴。真上が空いていて、左右どちらかに降り立てること
    const above = BUILDING.floors[i + 1];
    if (!above) continue;
    const ceilingRow = above.standRow + 1;
    const gaps = slabGapsAt(ceilingRow);
    if (gaps.length === 0) {
      out.push({ level: 'error', text: `${above.name}へ上がる穴が無い`, at: null });
      continue;
    }
    if (gaps.length < 2) {
      out.push({
        level: 'warn',
        text: `${above.name}への穴が1マス幅。キャットタワーの踊り場が収まらない（§10.15）`,
        at: { c: gaps[0], r: ceilingRow },
      });
    }
    for (const gap of gaps) {
      if (tileAt(grid, gap, ceilingRow - 1) !== EMPTY) {
        out.push({ level: 'error', text: `穴の真上が塞がっている`, at: { c: gap, r: ceilingRow - 1 } });
      }
    }
    const lo = Math.min(...gaps);
    const hi = Math.max(...gaps);
    if (!standable(lo - 1, ceilingRow - 1) && !standable(hi + 1, ceilingRow - 1)) {
      out.push({
        level: 'error',
        text: `穴の左右どちらにも降り立てない`,
        at: { c: lo, r: ceilingRow - 1 },
      });
    }
  }

  return out;
}

/** 階ごとに何の資材がどれだけ採れるか。偏りを見るため */
export interface FloorYield {
  id: string;
  name: string;
  mats: MaterialBundle;
  objects: number;
}

export function floorYields(world: World): FloorYield[] {
  const grid = world.state.grid;
  return BUILDING.floors.map((floor, i) => {
    const { standRow, topRow } = floorRows(i);
    const mats: MaterialBundle = {};
    let objects = 0;
    const add = (bundle: MaterialBundle): void => {
      for (const id of MATERIAL_IDS) {
        if (bundle[id]) mats[id] = (mats[id] ?? 0) + bundle[id]!;
      }
      objects++;
    };
    for (let c = 0; c < grid.cols; c++) {
      const kind = salvageKindOf(grid.salvage[keyOf(grid, c, standRow)]);
      if (kind) add(kind.yield);
    }
    for (const f of world.state.fixtures) {
      if (f.r > standRow || f.r < topRow) continue;
      const kind = fixtureKindOf(f.kind);
      if (kind) add(kind.yield);
    }
    return { id: floor.id, name: floor.name, mats, objects };
  });
}

/**
 * 重い検算。ボタンで明示的に走らせる。
 * 「この階から上へ、キャットタワー1本で道を通せるか」を総当たりで確かめる。
 * ここが通らないマップは、プレイヤーが詰む。
 */
export interface ClimbCheck {
  from: string;
  to: string;
  ok: boolean;
  anchor: { c: number; r: number; flip: boolean } | null;
}

export function checkClimbable(world: World): ClimbCheck[] {
  const grid = world.state.grid;
  const snapshot = JSON.stringify(world.state);
  const out: ClimbCheck[] = [];
  const start = keyOf(grid, world.state.entrance.c, world.state.entrance.r);

  /** 入口から、その行まで老猫の足で届くか */
  const reaches = (row: number): boolean => {
    const graph = graphFor(world, 'elder');
    if (!graph.nodes.has(start)) return false;
    for (const k of bfs(graph, [start]).keys()) {
      if (Math.floor(k / grid.cols) === row) return true;
    }
    return false;
  };

  try {
    // プレイヤーがたどる順に、1階ずつ実際にタワーを建てながら上がる。
    // 出発点を「その階のどこか」にすると、床の穴で切れた側から測ってしまう
    for (let i = 0; i < BUILDING.floors.length - 1; i++) {
      const floor = BUILDING.floors[i];
      const above = BUILDING.floors[i + 1];
      let found: ClimbCheck['anchor'] = null;

      for (let c = 1; c < grid.cols - 1 && !found; c++) {
        for (const flip of [false, true]) {
          if (!canPlaceItem(world, c, floor.standRow, 'tower', flip).ok) continue;

          const cells = itemCells('tower', c, floor.standRow, flip);
          const before = cells.map((cell) => grid.tiles[keyOf(grid, cell.c, cell.r)]);
          for (const cell of cells) grid.tiles[keyOf(grid, cell.c, cell.r)] = BUILT;
          world.graphs.clear();

          if (reaches(above.standRow)) {
            found = { c, r: floor.standRow, flip };
            break; // このタワーは残したまま次の階へ
          }
          cells.forEach((cell, n) => {
            grid.tiles[keyOf(grid, cell.c, cell.r)] = before[n];
          });
          world.graphs.clear();
        }
      }
      out.push({ from: floor.name, to: above.name, ok: found !== null, anchor: found });
      if (!found) break; // ここで詰まっているなら、上は測っても意味がない
    }
  } finally {
    world.state = JSON.parse(snapshot);
    rebuildGraphs(world);
  }
  return out;
}

// ---- 書き出し -----------------------------------------------------------

/** 今の盤面から floors.json を作り直す。地形は map（文字絵）として吐く */
export function exportFloors(world: World): string {
  const state = world.state;
  const grid = state.grid;

  const map: string[] = [];
  for (let r = 0; r < grid.rows; r++) {
    let line = '';
    for (let c = 0; c < grid.cols; c++) {
      line += tileAt(grid, c, r) === STRUCTURE ? '#' : '.';
    }
    map.push(line);
  }

  const salvage: { c: number; r: number; kind: string }[] = [];
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const kind = salvageKindOf(grid.salvage[keyOf(grid, c, r)]);
      if (kind) salvage.push({ c, r, kind: kind.id });
    }
  }

  const fixtures = [...state.fixtures]
    .sort((a, b) => a.r - b.r || a.c - b.c)
    .map((f) => ({ c: f.c, r: f.r, kind: f.kind }));

  const out = {
    note: BUILDING.note,
    mapLegend: BUILDING.mapLegend,
    map,
    salvage,
    fixturesNote: BUILDING.fixturesNote,
    fixtures,
    floors: BUILDING.floors,
    entrance: state.entrance,
    bowl: state.bowl,
    water: state.water,
    sun: state.sun,
    startMaterials: BUILDING.startMaterials,
    startCats: BUILDING.startCats,
  };
  return `${JSON.stringify(out, null, 2)}\n`;
}

/**
 * 今の思い出を memories.json の形で吐く。
 * 座標を持つのは「居着いて出る物」だけなので、そこだけ書き戻る。
 */
export function exportMemories(world: World): string {
  const src = MEMORY_DATA as { note: string; formNote: string; triggerNote: string; spots: unknown[] };
  const spots = world.state.memories.map((m) => {
    const out: Record<string, unknown> = {
      id: m.id,
      form: m.form,
      kind: m.kind,
      trigger: m.trigger,
    };
    if (m.trigger.on === 'dwell' || m.trigger.on === 'floor') {
      out.c = m.c;
      out.r = m.r;
    }
    if (m.title) out.title = m.title;
    out.line = m.line;
    return out;
  });
  return `${JSON.stringify({ note: src.note, formNote: src.formNote, triggerNote: src.triggerNote, spots }, null, 2)}\n`;
}

/** UI から色を引くため。materials.json を debug 側で読み直さないで済むように */
export const MATERIAL_LIST = MATERIALS;
